// PendingPlanChange Component
// Displays notice when a plan downgrade is scheduled for the end of the billing period

import { FiAlertCircle, FiCalendar, FiTrendingDown } from 'react-icons/fi';
import type { NextBillingInfo } from '../../../api/billing';
import styles from './PendingPlanChange.module.css';

import { logger } from '../../../utils/logger';

interface PendingPlanChangeProps {
  nextBilling: NextBillingInfo;
  currentPlanName: string;
}

export function PendingPlanChange({ nextBilling, currentPlanName }: PendingPlanChangeProps) {
  if (!nextBilling.has_pending_change || !nextBilling.pending_change) {
    return null;
  }

  const { pending_change } = nextBilling;
  const isDowngrade = pending_change.reason === 'downgrade';

  // Debug logging to see what backend is returning
  logger.log('🔍 PendingPlanChange Debug:', {
    currentPlanName,
    from_plan_name: pending_change.from_plan_name,
    from_credits: pending_change.from_credits_per_month,
    pending_plan_name: pending_change.pending_plan_name,
    pending_credits: pending_change.pending_credits_per_month,
    effective_date: pending_change.effective_date,
    reason: pending_change.reason,
  });

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className={`${styles.notice} ${isDowngrade ? styles.downgrade : styles.upgrade}`}>
      <div className={styles.header}>
        <div className={styles.icon}>
          {isDowngrade ? <FiTrendingDown /> : <FiAlertCircle />}
        </div>
        <h3 className={styles.title}>
          {isDowngrade ? 'Plan Downgrade Scheduled' : 'Plan Change Scheduled'}
        </h3>
      </div>

      <div className={styles.content}>
        <p className={styles.message}>
          Your plan will change from <strong>{pending_change.from_plan_name || currentPlanName}</strong>{' '}
          ({(pending_change.from_credits_per_month || 0).toLocaleString()} credits/mo) to{' '}
          <strong>{pending_change.pending_plan_name}</strong>{' '}
          ({pending_change.pending_credits_per_month.toLocaleString()} credits/mo) on{' '}
          <strong>{formatDate(pending_change.effective_date)}</strong>.
        </p>

        <div className={styles.details}>
          <div className={styles.detailItem}>
            <FiCalendar className={styles.detailIcon} />
            <div>
              <span className={styles.detailLabel}>Effective Date:</span>
              <span className={styles.detailValue}>{formatDate(pending_change.effective_date)}</span>
            </div>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Credits after change:</span>
            <span className={styles.detailValue}>
              {pending_change.pending_credits_per_month.toLocaleString()} credits/month
            </span>
          </div>
        </div>

        {isDowngrade && (
          <p className={styles.note}>
            💡 You'll keep your current credits until the end of your billing period.
            You can upgrade anytime to cancel this downgrade.
          </p>
        )}
      </div>
    </div>
  );
}

